import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { useMultiSelectActions } from "./overlays/useMultiSelectActions";
import { IndicatorLegend, ChartSplitter } from "../../lib/core";
import { calculateSplitterBounds } from "../../lib/core/hooks/paneSizingHelpers";
import { createDrawingSelectionToolbarModel, resolveDrawingSelectionToolbarPosition } from "@vninvestcharts/engine-drawing";
import { FloatingSelectionToolbar } from "@vninvestcharts/engine-react";
import { SettingsModal } from "../components/settings/SettingsModal";
import { SnapshotPreviewModal } from "../components/settings/SnapshotPreviewModal";
import FloatingDrawingInspector from "../components/drawing/FloatingDrawingInspector";
import InlineTextEditor from "../components/drawing/InlineTextEditor";
import { PaneHeaderWithPickerWrapper } from "./overlays/PaneHeaderWrapper";
import { buildLegendSeriesValue, PANE_HEADER_AXIS_GAP_PX, resolveGlobalLeftAxisWidth, resolveHasLeftAxis, resolveLeftAxisWidth, resolveRailReservePx } from "./overlays/overlayHelpers";
import { useLegendChrome, useCompareLegendNode } from "./overlays/useLegendChrome";
import { useDrawingDockNode } from "./overlays/useDrawingDockNode";
import { buildDockObjectsContent, buildDockPropertiesContent, buildDockTemplatesContent } from "../components/dock/drawingDockContentBuilders";
export { buildLegendSeriesValue, resolveGlobalLeftAxisWidth, resolveLeftAxisWidth };
export function useWidgetOverlays({ useForkRuntime, visiblePanes, paneSnapshotMap, paneSnapshots, size, hoveredPaneId, hoveredDatum, claimForkPointerOwner, releaseForkPointerOwner, handlePaneToggleVisible, handlePaneRemove, handlePaneAddSeries, handlePaneEditSeries, handlePaneToggleSeries, handlePaneRemoveSeries, localizePane, localAvailable, localHeights, localApplyDelta, localEqualizePair, localResetToDefault, renderPaneToolbar, renderPaneSplitter, renderDrawingChrome, toolbarLocked = false, showToolbar = true, drawingWorkspace, resolvedSelectedDrawing, resolvedLegacyVisibleRange, effectiveForkVisibleRange, magnetSensitivity, isDark, plotData, visibleDomainRef, cloneSelectedDrawing, toggleSelectedLock, handleStartTextEdit, isEditingText, drawingTextDraft, setDrawingTextDraft, handleCommitTextEdit, handleCancelTextEdit, bringSelectedToFront, sendSelectedToBack, showDrawingDock, renderDrawingDock, defaultDrawingDockTab, activeSettingsOpen, activeOnSettingsClose, dataAdapterName, onDataAdapterChange, localSelectedPaneId, setLocalSelectedPaneId, activeMaxVisiblePanes, setLocalMaxVisiblePanes, activeMaxVisibleBars, setLocalMaxVisibleBars, activeShowNonTradingDays, setLocalShowNonTradingDays, activeShowDrawingPriceMarkers, setLocalShowDrawingPriceMarkers, activeEnableWhale, setLocalEnableWhale, activeWhaleThreshold, setLocalWhaleThreshold, activeEnableCVD, setLocalEnableCVD, activeCvdMode, setLocalCvdMode, themeState, activeTimezone, setLocalTimezone, onTimezoneChange, activeAlertAudioEnabled, setLocalAlertAudioEnabled, previewImageDataUrl, setPreviewImageDataUrl, renderSettings, renderPaperTradePanel, showAdvisoryPanel, advisoryResult, isAdvisoryLoading, onAdvisoryRefresh, showTapePanel, tapeEntries, isTapeLoading, tapeFilterBillions, onTapeFilterChange, onPaperTradePanelClose, showDrawingPriceMarkers, resolvedForkPriceMarkerSelection, t, onDrawingsChange, showTimeScrollbar, onShowTimeScrollbarChange, overlayTimeframe, setOverlayTimeframe, overlayStyle, setOverlayStyle, onExportLayout, onImportLayout, portalAdapter, storageAdapter, themePreset, chartId, symbol, timeframe, comparisonOverlays, onComparisonRemove, onComparisonUpdate, legendSeriesSuffix, legendPanes, onQuickAddSeries, onQuickRemoveSeries, compareLegendSlot = "embedded", seriesTier, }) {
    const handleToolbarAction = useCallback((actionId) => {
        switch (actionId) {
            case "clone":
                cloneSelectedDrawing();
                break;
            case "toggleLock":
                toggleSelectedLock();
                break;
            case "toggleVisibility":
                if (resolvedSelectedDrawing && drawingWorkspace) {
                    drawingWorkspace.interaction.updateDrawing(resolvedSelectedDrawing.id, {
                        visible: resolvedSelectedDrawing.visible === false,
                    });
                }
                break;
            case "editText":
                handleStartTextEdit(resolvedSelectedDrawing?.text ?? "");
                break;
            case "bringToFront":
                bringSelectedToFront();
                break;
            case "sendToBack":
                sendSelectedToBack();
                break;
            case "delete":
                if (resolvedSelectedDrawing && drawingWorkspace) {
                    const nextDrawings = drawingWorkspace.interaction.allDrawings.filter((d) => d.id !== resolvedSelectedDrawing.id);
                    drawingWorkspace.interaction.replaceDrawings(nextDrawings);
                    drawingWorkspace.selectDrawing(null);
                }
                break;
            case "openProperties":
                drawingWorkspace?.openDock("properties");
                break;
            default:
                break;
        }
    }, [
        cloneSelectedDrawing,
        toggleSelectedLock,
        resolvedSelectedDrawing,
        drawingWorkspace,
        handleStartTextEdit,
        bringSelectedToFront,
        sendSelectedToBack,
    ]);
    // S1.1/D-01 (2026-09-24): the window keydown Delete/Backspace channel lived
    // here and double-fired with useWidgetDrawings (2 history entries, 1 undo
    // no-op). useWidgetDrawings is now the SOLE Delete/Backspace channel —
    // do NOT re-add a global Delete listener in this file. Toolbar buttons,
    // dock and inspector keep calling handleToolbarAction("delete") directly.
    // Legend chrome + compare node live in overlays/useLegendChrome (D0 split).
    const legendChrome = useLegendChrome(t);
    const renderCompareLegendNode = useCompareLegendNode({
        compareLegendSlot,
        visiblePanes,
        legendPanes,
        localizePane,
        legendSeriesSuffix,
        handlePaneToggleSeries,
        handlePaneRemoveSeries,
        handlePaneEditSeries,
        legendChrome,
        hoveredDatum,
    });
    const paneToolbarNodes = useMemo(() => {
        // VOL_FIX: global left-gutter offset — matches the engine plot shift
        // (PaneLayoutEngine offsets plotBounds for ALL panes when ANY pane
        // owns a left slot, while axisBounds.left stays per-pane). Per-pane
        // width left the Vol header at padding 0 inside the left-axis zone,
        // under the docked drawing toolbar.
        // DOCK_FIX: the overlay layer is anchored at the OUTER container origin
        // while the engine measures the INNER (rail-reserved) box — every
        // snapshot-anchored overlay shifts right by the same reserve the chart
        // column uses (44 locked / 10 auto-hide grip / 0 with left axis).
        const dockOffset = resolveRailReservePx(showToolbar, toolbarLocked, resolveHasLeftAxis(paneSnapshotMap.values(), visiblePanes));
        const globalLeftAxisWidth = resolveGlobalLeftAxisWidth(paneSnapshotMap.values());
        return visiblePanes.map((pane) => {
            const snapshot = paneSnapshotMap.get(pane.id);
            if (!snapshot)
                return null;
            const hovered = hoveredPaneId === pane.id;
            const actions = {
                toggleVisibility: () => handlePaneToggleVisible(pane.id),
                removePane: () => handlePaneRemove(pane.id),
                addSeries: () => handlePaneAddSeries(pane.id),
                toggleSeries: (seriesType, seriesIndex) => handlePaneToggleSeries(pane.id, seriesType, seriesIndex),
                removeSeries: (seriesType, seriesIndex) => handlePaneRemoveSeries(pane.id, seriesType, seriesIndex),
                editSeries: () => handlePaneEditSeries(pane.id),
            };
            const legendPane = legendPanes?.find((lp) => lp.id === pane.id) ?? localizePane(pane);
            const paneSuffix = legendSeriesSuffix ? (idx) => legendSeriesSuffix(pane.id, idx) : undefined;
            // UI-P4: flush-left when no left axis anywhere; when one appears,
            // every pane header sits just OUTSIDE the axis frames with a small
            // uniform gap (global max — a Vol pane without its own left series
            // still shifts, matching the global plot offset; snapshots flow live).
            const leftAxisWidth = globalLeftAxisWidth;
            const rightAxisWidth = snapshot.axisBounds?.right?.width ?? 48;
            const paneHeaderStyle = {
                paddingLeft: leftAxisWidth > 0 ? `calc(${leftAxisWidth}px + ${PANE_HEADER_AXIS_GAP_PX}px)` : 0,
                paddingRight: `calc(${rightAxisWidth}px + ${PANE_HEADER_AXIS_GAP_PX}px)`,
            };
            const legendStyle = {
                left: `calc(${leftAxisWidth}px - 3mm)`,
                right: `calc(${rightAxisWidth}px + 3mm)`,
            };
            const wrapPaneHeader = (header, legend) => (_jsxs("div", { className: `rsc-pane-wrap${hovered ? " rsc-pane-wrap--active" : ""}`, style: {
                    position: "absolute",
                    top: snapshot.bounds.top,
                    left: snapshot.bounds.left + dockOffset,
                    width: snapshot.bounds.width,
                    height: snapshot.bounds.height,
                    pointerEvents: "none",
                    zIndex: 40,
                }, children: [_jsx("div", { style: { pointerEvents: "auto", width: "100%", minHeight: 24 }, children: header }), legend] }, pane.id));
            if (renderPaneToolbar) {
                const toolbarEl = renderPaneToolbar({
                    pane: localizePane(pane),
                    snapshot,
                    hovered,
                    actions,
                    paneHeaderStyle,
                });
                const embeddedLegend = (_jsx(IndicatorLegend, { pane: legendPane, onToggleSeries: actions.toggleSeries, onRemoveSeries: actions.removeSeries, onEditSeries: actions.editSeries, icons: legendChrome.icons, labels: legendChrome.labels, seriesSuffix: paneSuffix, seriesValue: hoveredDatum ? (_seriesType, idx) => buildLegendSeriesValue(hoveredDatum, legendPane.series[idx]) : undefined, embedded: true, hiddenTypes: compareLegendSlot === "statusline" ? ["Comparison"] : undefined }));
                // Preserve the custom toolbar's own children (e.g. interactive buttons
                // bound to `actions`) and append the indicator legend after them.
                // Replacing children would silently discard custom interactivity.
                const prevChildren = toolbarEl.props?.children;
                const withLegend = React.isValidElement(toolbarEl)
                    ? React.cloneElement(toolbarEl, {
                        children: prevChildren === undefined ? embeddedLegend : _jsxs(_Fragment, { children: [prevChildren, embeddedLegend] }),
                    })
                    : toolbarEl;
                return wrapPaneHeader(withLegend, undefined);
            }
            return wrapPaneHeader(_jsx(PaneHeaderWithPickerWrapper, { pane: localizePane(pane), onToggleVisible: actions.toggleVisibility, onRemove: actions.removePane, onQuickAddSeries: onQuickAddSeries ? (series) => onQuickAddSeries(pane.id, series) : undefined, onQuickRemoveSeries: onQuickRemoveSeries ? (type) => onQuickRemoveSeries(pane.id, type) : undefined, style: paneHeaderStyle, children: _jsx(IndicatorLegend, { pane: legendPane, onToggleSeries: actions.toggleSeries, onRemoveSeries: actions.removeSeries, onEditSeries: actions.editSeries, icons: legendChrome.icons, labels: legendChrome.labels, seriesSuffix: paneSuffix, seriesValue: hoveredDatum ? (_seriesType, idx) => buildLegendSeriesValue(hoveredDatum, legendPane.series[idx]) : undefined, embedded: true, hiddenTypes: compareLegendSlot === "statusline" ? ["Comparison"] : undefined }) }), undefined);
        });
    }, [visiblePanes, paneSnapshotMap, hoveredPaneId, hoveredDatum, handlePaneToggleVisible, handlePaneRemove, handlePaneAddSeries, handlePaneEditSeries, handlePaneToggleSeries, handlePaneRemoveSeries, localizePane, renderPaneToolbar, legendPanes, legendSeriesSuffix, onQuickAddSeries, onQuickRemoveSeries, toolbarLocked, showToolbar, t, legendChrome, compareLegendSlot]);
    const paneSplitterNodes = useMemo(() => {
        // Same outer-layer origin as pane headers (see DOCK_FIX above).
        const dockOffset = resolveRailReservePx(showToolbar, toolbarLocked, resolveHasLeftAxis(paneSnapshotMap.values(), visiblePanes));
        return visiblePanes.slice(0, -1).map((pane, index) => {
            const snapshot = paneSnapshotMap.get(pane.id);
            if (!snapshot)
                return null;
            if (renderPaneSplitter) {
                return (_jsx("div", { "data-rsc-fork-pointer-owner": "pane-splitter", onPointerDown: () => claimForkPointerOwner("pane-splitter"), onPointerUp: () => releaseForkPointerOwner("pane-splitter"), onPointerCancel: () => releaseForkPointerOwner("pane-splitter"), style: { display: "contents" }, children: renderPaneSplitter({
                        pane,
                        snapshot,
                        splitterIndex: index,
                        top: snapshot.plotBounds.top + snapshot.plotBounds.height,
                        left: snapshot.plotBounds.left + dockOffset,
                        right: snapshot.plotBounds.left + snapshot.plotBounds.width + dockOffset,
                    }) }, pane.id));
            }
            const topH = localHeights[index] ?? 0;
            const bottomH = localHeights[index + 1] ?? 0;
            const { minDeltaY, maxDeltaY } = calculateSplitterBounds(visiblePanes[index], visiblePanes[index + 1], topH, bottomH);
            const handleEqualize = () => {
                if (localEqualizePair)
                    localEqualizePair(index);
                else
                    localResetToDefault();
            };
            return (_jsx("div", { "data-rsc-fork-pointer-owner": "pane-splitter", onPointerDown: () => claimForkPointerOwner("pane-splitter"), onPointerUp: () => releaseForkPointerOwner("pane-splitter"), onPointerCancel: () => releaseForkPointerOwner("pane-splitter"), style: { display: "contents" }, children: _jsx(ChartSplitter, { splitterIndex: index, available: localAvailable, onCommitDelta: localApplyDelta, onDoubleClick: handleEqualize, minDeltaY: minDeltaY, maxDeltaY: maxDeltaY, topHeight: topH, bottomHeight: bottomH, title: t("pane.splitter.tooltip"), ariaLabel: t("pane.splitter.ariaLabel", { index: index + 1 }), badgeTopLabel: t("pane.splitter.badgeTop") ?? "Top", badgeBottomLabel: t("pane.splitter.badgeBottom") ?? "Bottom", style: {
                        position: "absolute",
                        top: snapshot.plotBounds.top + snapshot.plotBounds.height,
                        left: snapshot.plotBounds.left + dockOffset,
                        width: snapshot.plotBounds.width,
                    } }) }, pane.id));
        });
    }, [visiblePanes, paneSnapshotMap, localAvailable, localApplyDelta, localEqualizePair, localResetToDefault, localHeights, renderPaneSplitter, toolbarLocked, showToolbar, t]);
    // S5.2: measured floating-bar rect (real size for clamp math; null =
    // 248x42 fallback). 1px-change guard prevents measure loops.
    const [measuredToolbarSize, setMeasuredToolbarSize] = useState(null);
    const handleMeasuredToolbarSize = useCallback((size) => {
        setMeasuredToolbarSize((prev) => (prev && Math.abs(prev.width - size.width) < 1 && Math.abs(prev.height - size.height) < 1 ? prev : size));
    }, []);
    // S1/BUG-1: multi-select actions (hook at top level; consumed in the memo below).
    // Anchors on the first-selected drawing via the existing single pipeline (D-04 option a).
    const multiSelectedIds = drawingWorkspace?.state.selectedIds ?? [];
    const multiDrawings = drawingWorkspace?.interaction.allDrawings ?? [];
    const multiActions = useMultiSelectActions({
        drawingWorkspace: drawingWorkspace ?? null,
        selectedIds: multiSelectedIds,
        drawings: multiDrawings,
        t,
    });
    const isMultiSelectBar = multiSelectedIds.length > 1;
    const drawingChromeNode = useMemo(() => {
        if (renderDrawingChrome) {
            return (_jsx(_Fragment, { children: renderDrawingChrome({
                    activeTool: drawingWorkspace.state.activeTool,
                    drawingWorkspace,
                    isDark,
                    magnetSensitivity,
                    paneSnapshots,
                    selectedDrawing: resolvedSelectedDrawing,
                    visibleRange: resolvedLegacyVisibleRange,
                    size,
                }) }));
        }
        if (!resolvedSelectedDrawing)
            return null;
        if (isEditingText && ["text", "callout", "priceLabel"].includes(resolvedSelectedDrawing.type))
            return null;
        const labels = {
            clone: t("drawing.clone"),
            lock: t("drawing.lock"),
            unlock: t("drawing.unlock"),
            hide: t("drawing.hide"),
            show: t("drawing.show"),
            edit: t("drawing.edit"),
            bringToFront: t("drawing.bringToFront"),
            sendToBack: t("drawing.sendToBack"),
            delete: t("drawing.delete"),
            openProperties: t("drawing.properties"),
        };
        const toolbarModel = createDrawingSelectionToolbarModel(resolvedSelectedDrawing, labels);
        if (!toolbarModel)
            return null;
        const position = resolveDrawingSelectionToolbarPosition({
            drawing: resolvedSelectedDrawing,
            paneSnapshots,
            plotData,
            visibleDomain: visibleDomainRef.current,
            shellSize: size,
            // S5.2: clamp with the measured bar rect when known (248x42 fallback).
            ...(measuredToolbarSize ? { toolbarSize: measuredToolbarSize } : {}),
        });
        if (!position)
            return null;
        return (_jsx(FloatingSelectionToolbar, { model: isMultiSelectBar ? null : toolbarModel, position: position, ariaLabel: t("drawing.inspectorTitle"), isDark: isDark, onAction: handleToolbarAction, drawing: resolvedSelectedDrawing, selectedCount: isMultiSelectBar ? multiSelectedIds.length : undefined, onDeleteAll: isMultiSelectBar ? multiActions.handleDeleteAll : undefined, onLockAll: isMultiSelectBar ? (() => multiActions.handleLockAll()) : undefined, onSetVisibleAll: isMultiSelectBar ? multiActions.handleSetVisibleAll : undefined, onBatchColor: isMultiSelectBar ? multiActions.handleBatchColor : undefined, onCloneAll: isMultiSelectBar ? multiActions.handleCloneAll : undefined, onGroupAll: isMultiSelectBar ? multiActions.handleGroupToggleAll : undefined, allLocked: isMultiSelectBar ? multiActions.allLocked : undefined, allHidden: isMultiSelectBar ? multiActions.allHidden : undefined, onUpdate: (patch) => {
                drawingWorkspace?.interaction.updateDrawing(resolvedSelectedDrawing.id, patch);
            }, labels: {
                deleteAll: t("drawing.toolbar.deleteAll"),
                lockAll: t("drawing.toolbar.lockAll"),
                unlockAll: t("drawing.toolbar.unlockAll"),
                hideAll: t("drawing.toolbar.hideAll"),
                showAll: t("drawing.toolbar.showAll"),
                batchColor: t("drawing.toolbar.batchColor"),
                duplicate: t("drawing.selection.duplicate"),
                group: t("drawing.selection.group"),
                selectedCountText: t("drawing.toolbar.selectedCount", { count: 1 }),
                dragHint: t("drawing.toolbar.dragHint"),
                all: t("drawing.toolbar.all"),
                pickColor: t("drawing.toolbar.pickColor"),
                strokeWidth: t("drawing.toolbar.strokeWidth"),
                lineStyle: t("drawing.toolbar.lineStyle"),
                solid: t("drawing.toolbar.solid"),
                dashed: t("drawing.toolbar.dashed"),
                dotted: t("drawing.toolbar.dotted"),
                pointLabelPrefix: t("drawing.toolbar.point"),
            }, onMeasuredSize: handleMeasuredToolbarSize }));
    }, [renderDrawingChrome, drawingWorkspace, isDark, magnetSensitivity, paneSnapshots, resolvedSelectedDrawing, resolvedLegacyVisibleRange, size, t, plotData, visibleDomainRef, handleToolbarAction, measuredToolbarSize, handleMeasuredToolbarSize, isMultiSelectBar, multiSelectedIds, multiActions]);
    const handleDeleteDrawing = useCallback(() => {
        if (!resolvedSelectedDrawing || !drawingWorkspace)
            return;
        const nextDrawings = drawingWorkspace.interaction.allDrawings.filter((d) => d.id !== resolvedSelectedDrawing.id);
        drawingWorkspace.interaction.replaceDrawings(nextDrawings);
        drawingWorkspace.selectDrawing(null);
    }, [resolvedSelectedDrawing, drawingWorkspace]);
    const handleToggleVisibility = useCallback(() => {
        if (!resolvedSelectedDrawing || !drawingWorkspace)
            return;
        drawingWorkspace.interaction.updateDrawing(resolvedSelectedDrawing.id, {
            visible: resolvedSelectedDrawing.visible === false,
        });
    }, [resolvedSelectedDrawing, drawingWorkspace]);
    const handleCloseFloatingInspector = useCallback(() => {
        drawingWorkspace?.closeDock();
    }, [drawingWorkspace]);
    const floatingInspectorNode = useMemo(() => {
        if (!drawingWorkspace || !drawingWorkspace.state.dock.isOpen || drawingWorkspace.state.dock.activeTab !== "properties" || !resolvedSelectedDrawing) {
            return null;
        }
        return (_jsx(FloatingDrawingInspector, { drawing: resolvedSelectedDrawing, isDark: isDark, t: t, textEditor: ["text", "callout", "priceLabel"].includes(resolvedSelectedDrawing.type) ? {
                active: isEditingText,
                value: drawingTextDraft,
                labels: {
                    title: t("drawing.textEditorTitle"),
                    label: t("drawing.textEditorLabel"),
                    placeholder: t("drawing.textEditorPlaceholder"),
                    edit: t("drawing.textEditorEdit"),
                    save: t("drawing.textEditorSave"),
                    cancel: t("drawing.textEditorCancel"),
                    empty: t("drawing.textEditorEmpty"),
                },
                onChange: setDrawingTextDraft,
                onStartEdit: () => handleStartTextEdit(resolvedSelectedDrawing.text ?? ""),
                onCommit: () => handleCommitTextEdit((draft) => {
                    drawingWorkspace.interaction.updateDrawing(resolvedSelectedDrawing.id, { text: draft });
                }),
                onCancel: handleCancelTextEdit,
            } : undefined, onClose: handleCloseFloatingInspector, onUpdate: (patch) => {
                drawingWorkspace.interaction.updateDrawing(resolvedSelectedDrawing.id, patch);
            }, onDelete: handleDeleteDrawing, onClone: cloneSelectedDrawing, onToggleLock: toggleSelectedLock, onToggleVisible: handleToggleVisibility, onBringToFront: bringSelectedToFront, onSendToBack: sendSelectedToBack, plotData: plotData }));
    }, [drawingWorkspace, resolvedSelectedDrawing, isDark, t, handleCloseFloatingInspector, cloneSelectedDrawing, toggleSelectedLock, handleToggleVisibility, handleDeleteDrawing, bringSelectedToFront, sendSelectedToBack, plotData, isEditingText, drawingTextDraft, setDrawingTextDraft, handleStartTextEdit, handleCommitTextEdit, handleCancelTextEdit]);
    // S3 (P5 parity F2): shared builders (single + multi dùng chung, Rule 6).
    const dockTextEdit = useMemo(() => ({
        isEditingText,
        drawingTextDraft,
        setDrawingTextDraft,
        onStartTextEdit: handleStartTextEdit,
        onCommitTextEdit: handleCommitTextEdit,
        onCancelTextEdit: handleCancelTextEdit,
    }), [
        isEditingText,
        drawingTextDraft,
        setDrawingTextDraft,
        handleStartTextEdit,
        handleCommitTextEdit,
        handleCancelTextEdit,
    ]);
    const dockSelectedActions = useMemo(() => ({
        onDeleteDrawing: handleDeleteDrawing,
        onCloneSelected: cloneSelectedDrawing,
        onToggleSelectedLock: toggleSelectedLock,
        onToggleSelectedVisibility: handleToggleVisibility,
        onBringSelectedToFront: bringSelectedToFront,
        onSendSelectedToBack: sendSelectedToBack,
    }), [
        handleDeleteDrawing,
        cloneSelectedDrawing,
        toggleSelectedLock,
        handleToggleVisibility,
        bringSelectedToFront,
        sendSelectedToBack,
    ]);
    const dockPropertiesContent = useMemo(() => buildDockPropertiesContent({
        workspace: drawingWorkspace,
        selected: resolvedSelectedDrawing,
        t,
        textEdit: dockTextEdit,
        actions: dockSelectedActions,
    }), [
        resolvedSelectedDrawing,
        drawingWorkspace,
        t,
        dockTextEdit,
        dockSelectedActions,
    ]);
    const dockObjectsContent = useMemo(() => buildDockObjectsContent({
        workspace: drawingWorkspace,
        selected: resolvedSelectedDrawing,
        t,
        textEdit: dockTextEdit,
        actions: dockSelectedActions,
    }), [drawingWorkspace, resolvedSelectedDrawing, t, dockTextEdit, dockSelectedActions]);
    const dockTemplatesContent = useMemo(() => buildDockTemplatesContent({
        workspace: drawingWorkspace,
        selected: resolvedSelectedDrawing,
        t,
        textEdit: dockTextEdit,
        actions: dockSelectedActions,
    }), [resolvedSelectedDrawing, drawingWorkspace, t, dockTextEdit, dockSelectedActions]);
    // Drawing dock node lives in overlays/useDrawingDockNode (D0 split).
    const drawingDockNode = useDrawingDockNode({
        showDrawingDock,
        drawingWorkspace,
        renderDrawingDock,
        t,
        dockPropertiesContent,
        dockObjectsContent,
        dockTemplatesContent,
    });
    const settingsModalNode = useMemo(() => {
        if (!activeSettingsOpen)
            return null;
        return (_jsx(SettingsModal, { open: activeSettingsOpen, onClose: activeOnSettingsClose, dataAdapterName: dataAdapterName, onDataAdapterChange: onDataAdapterChange, selectedPaneId: localSelectedPaneId, onSelectedPaneIdChange: setLocalSelectedPaneId, maxVisiblePanes: activeMaxVisiblePanes, onMaxVisiblePanesChange: setLocalMaxVisiblePanes, maxVisibleBars: activeMaxVisibleBars, onMaxVisibleBarsChange: setLocalMaxVisibleBars, showNonTradingDays: activeShowNonTradingDays, onShowNonTradingDaysChange: setLocalShowNonTradingDays, showTimeScrollbar: showTimeScrollbar, onShowTimeScrollbarChange: onShowTimeScrollbarChange, showDrawingPriceMarkers: activeShowDrawingPriceMarkers, onShowDrawingPriceMarkersChange: setLocalShowDrawingPriceMarkers, enableWhale: activeEnableWhale, onEnableWhaleChange: setLocalEnableWhale, whaleThreshold: activeWhaleThreshold, onWhaleThresholdChange: setLocalWhaleThreshold, enableCVD: activeEnableCVD, onEnableCVDChange: setLocalEnableCVD, cvdMode: activeCvdMode, onCVDModeChange: setLocalCvdMode, isDark: isDark, toggleTheme: themeState.toggleTheme, onThemePresetChange: themeState.setTheme, timezone: activeTimezone, onTimezoneChange: (next) => {
                setLocalTimezone(next);
                if (onTimezoneChange) {
                    onTimezoneChange(next);
                }
            }, alertAudioEnabled: activeAlertAudioEnabled, onAlertAudioEnabledChange: setLocalAlertAudioEnabled, storageAdapter: storageAdapter, portalAdapter: portalAdapter, themePreset: themePreset, chartId: chartId, symbol: symbol, timeframe: timeframe, overlayTimeframe: overlayTimeframe, onOverlayTimeframeChange: setOverlayTimeframe, overlayStyle: overlayStyle, onOverlayStyleChange: setOverlayStyle, onExportLayout: onExportLayout, onImportLayout: onImportLayout, comparisonOverlays: comparisonOverlays, onComparisonRemove: onComparisonRemove, onComparisonUpdate: onComparisonUpdate, seriesTier: seriesTier, onReset: () => {
                localResetToDefault();
                setLocalMaxVisiblePanes(6);
                setLocalMaxVisibleBars(40000);
                setLocalShowNonTradingDays(false);
                setLocalShowDrawingPriceMarkers(true);
                setLocalEnableWhale(true);
                setLocalWhaleThreshold(1000000);
                setLocalEnableCVD(false);
                setLocalCvdMode("oscillator");
                setLocalTimezone("Asia/Ho_Chi_Minh");
                setLocalAlertAudioEnabled(false);
                setOverlayTimeframe(undefined);
                setOverlayStyle("candle");
            } }));
    }, [activeSettingsOpen, renderSettings, activeOnSettingsClose, dataAdapterName, onDataAdapterChange, localSelectedPaneId, setLocalSelectedPaneId, activeMaxVisiblePanes, setLocalMaxVisiblePanes, activeMaxVisibleBars, setLocalMaxVisibleBars, activeShowNonTradingDays, setLocalShowNonTradingDays, showTimeScrollbar, onShowTimeScrollbarChange, activeShowDrawingPriceMarkers, setLocalShowDrawingPriceMarkers, activeEnableWhale, setLocalEnableWhale, activeWhaleThreshold, setLocalWhaleThreshold, activeEnableCVD, setLocalEnableCVD, activeCvdMode, setLocalCvdMode, isDark, themeState, activeTimezone, setLocalTimezone, onTimezoneChange, activeAlertAudioEnabled, setLocalAlertAudioEnabled, localResetToDefault, comparisonOverlays, onComparisonRemove, onComparisonUpdate, storageAdapter, portalAdapter, themePreset, chartId, symbol, timeframe, overlayTimeframe, setOverlayTimeframe, overlayStyle, setOverlayStyle, onExportLayout, onImportLayout]);
    const snapshotPreviewModalNode = useMemo(() => {
        if (!previewImageDataUrl)
            return null;
        return (_jsx(SnapshotPreviewModal, { isOpen: !!previewImageDataUrl, imageDataUrl: previewImageDataUrl, isDark: isDark, themePreset: themePreset, onClose: () => setPreviewImageDataUrl(null), onSave: () => {
                if (previewImageDataUrl) {
                    const a = document.createElement("a");
                    a.setAttribute("href", previewImageDataUrl);
                    a.setAttribute("download", "Chart-Viewport.png");
                    const container = portalAdapter?.getContainer?.() ?? (typeof document !== "undefined" ? document.body : null);
                    if (container) {
                        container.appendChild(a);
                        a.click();
                        container.removeChild(a);
                    }
                    setPreviewImageDataUrl(null);
                }
            }, portalAdapter: portalAdapter }));
    }, [previewImageDataUrl, isDark, setPreviewImageDataUrl, portalAdapter]);
    const handleTextInlineUpdate = useCallback((id, patch) => {
        drawingWorkspace?.interaction.updateDrawing(id, patch);
    }, [drawingWorkspace]);
    const handleTextInlineCommit = useCallback((commitCallback) => {
        handleCommitTextEdit(commitCallback);
        drawingWorkspace?.interaction.cancelDrawing();
    }, [handleCommitTextEdit, drawingWorkspace]);
    const handleTextInlineCancel = useCallback(() => {
        handleCancelTextEdit();
        drawingWorkspace?.interaction.cancelDrawing();
    }, [handleCancelTextEdit, drawingWorkspace]);
    const inlineEditorNode = useMemo(() => {
        if (!isEditingText || !resolvedSelectedDrawing)
            return null;
        return (_jsx(InlineTextEditor, { selectedDrawing: resolvedSelectedDrawing, paneSnapshots: paneSnapshots, isEditingText: isEditingText, drawingTextDraft: drawingTextDraft, setDrawingTextDraft: setDrawingTextDraft, handleCommitTextEdit: handleTextInlineCommit, handleCancelTextEdit: handleTextInlineCancel, handleUpdateDrawing: handleTextInlineUpdate, plotData: plotData, visibleDomainRef: visibleDomainRef, shellSize: size }));
    }, [isEditingText, resolvedSelectedDrawing, paneSnapshots, drawingTextDraft, setDrawingTextDraft, handleTextInlineCommit, handleTextInlineCancel, handleTextInlineUpdate, plotData, visibleDomainRef, size]);
    // DOCK_SSOT_B3 (RD-02): memo `hasRightSidebar` + resize fields đã gỡ — quyết định
    // mount giờ thuộc VNInvestCharts (`showDock !== false`) và width thuộc RightDockRail
    // (useSidebarResize nội bộ, cùng key `vnsc_dock_width`). `showDock` không còn vào hook.
    return {
        paneToolbarNodes,
        paneSplitterNodes,
        drawingChromeNode,
        drawingDockNode,
        floatingInspectorNode,
        inlineEditorNode,
        settingsModalNode,
        snapshotPreviewModalNode,
        renderCompareLegendNode,
    };
}
